import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AddedDocToSignResponse,
  AddedDocToSignResponseFromApi,
  EnablementDto,
  PetentData,
  PzService,
} from '@gk/gk-modules';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { AppTitleService } from '../../../../services/app-title/app-title.service';
import { AuthorizedPersonToRegistryView } from '../../../../services/authorized-person-to-registry-view/authorized-person-to-registry-view.model';
import { RangeType } from '../../../../services/parcel/parcel.model';
import { handleApiError } from '../../../../services/services.utils';
import { WebPortalService } from '../../../../services/web-portal/web-portal.service';
import {
  ApiTimeString,
  DateHelperUtil,
} from '../../../../utils/date-helper/date-helper.util';
import {
  NewRequestApiModel,
  NewRequestModel,
  PrzpZamowienieZakresRequestDto,
  TypeOfSharing,
} from './new-request.model';

@Injectable({
  providedIn: 'root',
})
export class NewRequestService {
  constructor(
    private httpClient: HttpClient,
    private appTitleService: AppTitleService,
    private webPortalService: WebPortalService,
    private pzService: PzService,
  ) {}

  private getRangeForApi(
    formValue: NewRequestModel,
  ): PrzpZamowienieZakresRequestDto {
    const parcelIds = (formValue.parcels || []).map((parcel) => parcel.uuid);
    const districtIds = (formValue.districts || []).map(
      (district) => district.districtId,
    );

    return new PrzpZamowienieZakresRequestDto(
      formValue.rangeType === RangeType.Parcels ? parcelIds : null,
      formValue.rangeType === RangeType.Districts ? districtIds : null,
      formValue.rangeType === RangeType.Wkt ? formValue.wkt : null,
      formValue.typeOfSharing === TypeOfSharing.Gml
        ? parseInt(formValue.dataBaseGml, 10)
        : null,
    );
  }

  newRequestFormModelToApiModel = (
    formValue: NewRequestModel,
    petent?: PetentData,
  ): NewRequestApiModel => {
    const isRegister = formValue.typeOfSharing === TypeOfSharing.Register;
    const shouldOmitAuthorizedPerson =
      this.isOmmitAuthorisedPersonFlagEnabled(formValue);

    return {
      IdPetenta: formValue.applicantId || petent?.id,
      CelId: formValue.purpose?.id,
      Par1: formValue.par1,
      Par2: formValue.par2,
      UpowaznieniInfo: shouldOmitAuthorizedPerson
        ? null
        : formValue.authorizedPersonsToRegistryView?.length
          ? formValue.authorizedPersonsToRegistryView.map((person) =>
              AuthorizedPersonToRegistryView.fromAppToApi(person),
            )
          : null,
      CzasDostepu: {
        CzasDostepuOd: formValue.period?.from
          ? DateHelperUtil.ngbDateToApiDateTime(formValue.period.from)
          : undefined,
        CzasDostepuDo: formValue.period?.to
          ? DateHelperUtil.ngbDateToApiDateTime(
              formValue.period.to,
              ApiTimeString.End,
            )
          : undefined,
      },
      RejPublId:
        isRegister && formValue.publicRegister
          ? parseInt(formValue.publicRegister, 10)
          : null,
      ZakresDaneOs: formValue.personalData,
      Zakres: this.getRangeForApi(formValue),
      Pelnomocnictwo: shouldOmitAuthorizedPerson
        ? null
        : EnablementDto.fromAppToApi(formValue.enablement),
      PodstawaId: shouldOmitAuthorizedPerson
        ? formValue.predefinedLegalInterest.id
        : null,
      SygnaturaZadPubl: formValue.publicTaskCaseReference,
      IsPorozumienie: formValue.isAgreement,
    };
  };

  public isOmmitAuthorisedPersonFlagEnabled(
    formValue: NewRequestModel,
  ): boolean {
    return formValue?.predefinedLegalInterest?.ommitAuthorisedPerson;
  }

  downloadRequest(pUuid: string): Observable<HttpResponse<Blob>> {
    return this.appTitleService.currentPortalRoute.pipe(
      filter((portalRoute) => !!portalRoute),
      switchMap((portalRoute) =>
        this.webPortalService.getWebPortalDataByCurrentRoute(portalRoute),
      ),
      switchMap((webPortal) => {
        const requestBody = {
          FileUuids: pUuid,
          PortalId: webPortal.portalId,
        };

        return this.httpClient
          .post('/api/interesant/przp/dokument/download', requestBody, {
            responseType: 'blob',
            observe: 'response',
          })
          .pipe(catchError(handleApiError));
      }),
    );
  }

  getResponseStatusMessage(error: string): string {
    return _.get(error, 'error.ResponseStatus.Message', '');
  }

  valideDtoAndAddToSign(
    formValue: NewRequestModel,
  ): Observable<AddedDocToSignResponse> {
    return this.pzService.getPetentData().pipe(
      switchMap((petent) =>
        this.httpClient.post<AddedDocToSignResponseFromApi>(
          '/api/interesant/przp/wniosek/addToSign',
          this.newRequestFormModelToApiModel(formValue, petent),
        ),
      ),
      map((addedDocToSignResponseFromApi) =>
        AddedDocToSignResponse.fromApiToApp(addedDocToSignResponseFromApi),
      ),
    );
  }
}
